import {
  PRMarinaArtboard,
  PRMarinaLayer,
  PRMarinaLayerStyle,
  PRMarinaNode,
  PRRect,
} from '@sketch-hq/sketch-web-renderer'
import {
  ArtboardPreset,
  Color,
  GridSettings,
  GroupLayerStyle,
  LayerStyle,
  LayoutSettings,
  SketchArtboardNode,
  SketchElementNode,
  SketchElementNodeBase,
  SketchElementNodeType,
  SketchLayerNode,
  StyleProperties,
  TextLayerStyle,
} from '../types'
import { getElementTypeFromPRMarinaLayer } from './getElementTypeFromPRMarinaLayer'
import {
  transformPresentationAppearance,
  transformPresentationBlur,
  transformPresentationBorderOptions,
  transformPresentationBorders,
  transformPresentationFileColor,
  transformPresentationFills,
  transformPresentationShadows,
  transformPresentationTextVerticalAlignment,
} from './presentationFileTransformUtils'
import { OverridableStyleProperties } from '../../inspectorDataType/Styles'

export function createSketchElementNode(
  marinaNode: PRMarinaNode,
  elementRenderingIndex: number,
  /**
   * The bounds of the root artboard.
   */
  rootNodeBounds: PRRect
): SketchElementNode {
  if (marinaNode.isArtboard()) {
    return createSketchArtboardNode(
      marinaNode,
      elementRenderingIndex,
      rootNodeBounds
    )
  }

  return createSketchLayerNode(
    marinaNode,
    elementRenderingIndex,
    rootNodeBounds
  )
}

export function getSketchElementBaseProperties(
  marinaNode: PRMarinaNode,
  elementRenderingIndex: number,

  /**
   * The bounds of the root artboard.
   */
  rootNodeBounds: PRRect
): SketchElementNodeBase {
  const prRectBounds = marinaNode.getAbsoluteBounds()

  return {
    prMarinaNode: marinaNode,
    identifier: marinaNode.getIdentifier(),
    name: marinaNode.getLayerName(),
    nodeUUID: marinaNode.getLayerUUID(),
    children: [],
    // We want the bounds to be relative to the artboard and not the canvas if the root of the tree is just an artboard.
    rootRelativeBounds: {
      x: prRectBounds.x - rootNodeBounds.x,
      y: prRectBounds.y - rootNodeBounds.y,
      width: prRectBounds.width,
      height: prRectBounds.height,
    },
    elementRenderingIndex,
  }
}

/** ARTBOARD */

export function createSketchArtboardNode(
  marinaNode: PRMarinaNode,
  elementRenderingIndex: number,
  /**
   * The bounds of the root artboard.
   */
  rootNodeBounds: PRRect
): SketchArtboardNode {
  const prMarinaArtboard = marinaNode.getArtboard()
  const prMarinaLayer = marinaNode.getLayer()

  if (!prMarinaArtboard) {
    throw new Error(
      "Can't create a SketchArtboardNode without a PRMarinaArtboard"
    )
  }

  if (!prMarinaLayer) {
    throw new Error("Can't create a SketchArtboardNode without a PRMarinaLayer")
  }

  const elementType = getElementTypeFromPRMarinaLayer(prMarinaLayer)

  if (elementType !== 'artboard' && elementType !== 'symbolMaster') {
    throw new Error('The layer type must be an artboard or a symbol master')
  }

  return {
    type: elementType,
    ...getSketchElementBaseProperties(
      marinaNode,
      elementRenderingIndex,
      rootNodeBounds
    ),
    identifier: marinaNode.getIdentifier(),
    gridSettings: getGridSettings(prMarinaArtboard),
    layoutSettings: getLayoutSettings(prMarinaArtboard),
    preset: getArtboardPreset(prMarinaArtboard),
    backgroundColor: getArtboardBackgroundColor(prMarinaLayer),
  }
}

function getGridSettings(prMarinaArtboard: PRMarinaArtboard): GridSettings {
  const defaultGridSettings: GridSettings = {
    isEnabled: false,
    size: 20,
    thickLineStep: 10,
  }
  const prArtboardGrid = prMarinaArtboard.getGrid()

  const gridSettings: GridSettings | undefined = prArtboardGrid
    ? {
        isEnabled: prArtboardGrid.isEnabled,
        size: prArtboardGrid.size,
        thickLineStep: prArtboardGrid.thickGridTimes,
      }
    : undefined

  return {
    ...defaultGridSettings,
    ...gridSettings,
  }
}

function getLayoutSettings(prMarinaArtboard: PRMarinaArtboard): LayoutSettings {
  const prArtboardLayout = prMarinaArtboard.getLayout()
  const defaultLayoutSettings: LayoutSettings = {
    columnWidth: 60,
    drawHorizontal: false,
    drawHorizontalLines: false,
    drawVertical: true,
    gutterHeight: 12,
    gutterWidth: 20,
    guttersOutside: true,
    horizontalOffset: 0,
    isEnabled: false,
    numberOfColumns: 5,
    numberOfRows: 0,
    rowHeightMultiplication: 5,
    totalWidth: 0,
  }

  const layoutSettings: LayoutSettings | undefined = prArtboardLayout
    ? {
        columnWidth: prArtboardLayout.columnWidth,
        drawHorizontal: prArtboardLayout.drawHorizontal,
        drawHorizontalLines: prArtboardLayout.drawHorizontalLines,
        drawVertical: prArtboardLayout.drawVertical,
        gutterHeight: prArtboardLayout.gutterHeight,
        gutterWidth: prArtboardLayout.gutterWidth,
        guttersOutside: prArtboardLayout.gutterOutside,
        horizontalOffset: prArtboardLayout.horizontalOffset,
        isEnabled: prArtboardLayout.isEnabled,
        numberOfColumns: prArtboardLayout.columnsCount,
        numberOfRows: 0,
        rowHeightMultiplication: prArtboardLayout.rowHeightMultiplication,
        totalWidth: prArtboardLayout.totalWidth,
      }
    : undefined

  return {
    ...defaultLayoutSettings,
    ...layoutSettings,
  }
}

function getArtboardPreset(prMarinaArtboard: PRMarinaArtboard): ArtboardPreset {
  const presetBounds = prMarinaArtboard.templateBounds

  return {
    name: prMarinaArtboard.templateName,
    width: presetBounds.width,
    height: presetBounds.height,
  }
}

function getArtboardBackgroundColor(
  prMarinaLayer: PRMarinaLayer
): Color | undefined {
  const artboardOverridableStyle = prMarinaLayer.getStyle()
  const layerStyle = artboardOverridableStyle?.getLayerStyle()
  const artboardFill = layerStyle?.fills.get(0)
  const prMarinaColor = artboardFill?.getColor()

  if (!prMarinaColor) {
    return undefined
  }

  return transformPresentationFileColor(prMarinaColor)
}

/** LAYER */

export function createSketchLayerNode(
  marinaNode: PRMarinaNode,
  elementRenderingIndex: number,
  /**
   * The bounds of the root artboard.
   */
  rootNodeBounds: PRRect
): SketchLayerNode {
  const prMarinaLayer = marinaNode.getLayer()
  if (!prMarinaLayer) {
    throw new Error("Can't create a SketchArtboardNode without a PRMarinaLayer")
  }
  const elementType = getElementTypeFromPRMarinaLayer(prMarinaLayer)
  if (
    elementType === 'artboard' ||
    elementType === 'symbolMaster' ||
    elementType === 'page'
  ) {
    throw new Error('The element type must be a layer')
  }

  /**
   * @todo finish all the todo in fixme inside
   * getLayerStyle
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const style = getLayerStyle(prMarinaLayer, elementType)

  return {
    type: elementType,
    ...getSketchElementBaseProperties(
      marinaNode,
      elementRenderingIndex,
      rootNodeBounds
    ),
    // style,
  }
}

function getLayerStyle(
  prMarinaLayer: PRMarinaLayer,
  elementType: SketchElementNodeType
): LayerStyle | TextLayerStyle {
  const styleInfo = prMarinaLayer.getStyle()
  const layerStyle = styleInfo?.getLayerStyle()
  const originalSharedStyleValues = styleInfo?.getOriginalSharedStyleValues()

  const isDirty = styleInfo?.isDirty ?? false

  const layerStyleProps = layerStyle
    ? getStyleProperties(layerStyle)
    : undefined

  const originalSharedStyleValuesProps = originalSharedStyleValues
    ? getStyleProperties(originalSharedStyleValues)
    : undefined

  if (elementType === 'group') {
    const groupStyle: GroupLayerStyle = {
      isDirty,
      fills: layerStyleProps?.fills ?? [],
      shadows: layerStyleProps?.shadows ?? [],
      appearance: layerStyleProps?.appearance,
    }

    return groupStyle
  }

  if (elementType === 'text') {
    // @TODO: Handle style.getTextAttributes

    const prVerticalTextAlignment = layerStyle?.verticalTextAlignment

    if (!prVerticalTextAlignment) {
      throw new Error(
        'Expected element of type text to have an verticalTextAlignment prop'
      )
    }

    const textStyle: TextLayerStyle = {
      isDirty,
      ...layerStyleProps,
      text: {
        verticalAlignment: transformPresentationTextVerticalAlignment(
          prVerticalTextAlignment
        ),
      },
    }

    if (originalSharedStyleValuesProps) {
      textStyle.originalSharedStyleValues = {
        ...originalSharedStyleValuesProps,
        text: undefined,
      }
    }

    return textStyle
  }

  return {
    isDirty,
    ...layerStyleProps,
    originalSharedStyleValues: originalSharedStyleValuesProps,
  }
}

export function getStyleProperties(
  prMarinaLayerStyle: PRMarinaLayerStyle
): StyleProperties | OverridableStyleProperties {
  const prBlur = prMarinaLayerStyle.getBlur()
  const prFills = prMarinaLayerStyle.fills
  const prAppearance = prMarinaLayerStyle.appearance
  const prBorders = prMarinaLayerStyle.borders
  const prShadows = prMarinaLayerStyle.shadows
  const prBorderOptions = prMarinaLayerStyle.borderOptions
  const prInnerShadows = prMarinaLayerStyle.innerShadows

  const blur = prBlur ? transformPresentationBlur(prBlur) : undefined
  const fills = prFills ? transformPresentationFills(prFills) : []
  const shadows = prShadows ? transformPresentationShadows(prShadows) : []
  const appearance = prAppearance
    ? transformPresentationAppearance(prAppearance)
    : undefined

  return {
    fills,
    blur,
    appearance,
    borders: prBorders ? transformPresentationBorders(prBorders) : [],
    borderOptions: prBorderOptions
      ? transformPresentationBorderOptions(prBorderOptions)
      : undefined,
    shadows,
    innerShadows: prInnerShadows
      ? transformPresentationShadows(prInnerShadows)
      : [],
  }
}
